import { useState, useEffect, useCallback } from 'react'
import {
  Clock,
  CheckCircle,
  XCircle,
  Check,
  X,
  Eye,
  Undo2,
  Info,
  Loader2,
  ChevronLeft,
  ChevronRight
} from 'lucide-react'
import { pointsApi, exchangesApi } from '../../lib/api'

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100]

const MedicalVerify = () => {
  const [records, setRecords] = useState<any[]>([])
  const [total, setTotal] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [confirmedRecords, setConfirmedRecords] = useState<any[]>([])
  const [confirmedTotal, setConfirmedTotal] = useState(0)
  const [confirmedPage, setConfirmedPage] = useState(1)
  const [confirmedPageSize, setConfirmedPageSize] = useState(10)
  const [selectedRecord, setSelectedRecord] = useState<any | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [actionType, setActionType] = useState<'confirm' | 'cancel' | 'revoke'>('confirm')
  const [cancelReason, setCancelReason] = useState('')
  const [activeTab, setActiveTab] = useState<'pending' | 'confirmed'>('pending')
  const [loading, setLoading] = useState(false)
  const [toast, setToast] = useState<{msg: string; type: 'error'|'success'}| null>(null)
  const [jumpInput, setJumpInput] = useState('')
  const [confirmedJumpInput, setConfirmedJumpInput] = useState('')

  // 权限
  const rawAuthUser = localStorage.getItem('auth_user')
  const authUser = rawAuthUser ? JSON.parse(rawAuthUser) : null
  const isAdmin = authUser?.role === 'admin'
  const isSupervisor = authUser?.role === 'medical' && authUser?.medicalRole === 'supervisor'
  const canOperate = isAdmin || isSupervisor

  const showToast = (msg: string, type: 'error'|'success' = 'error') => {
    setToast({msg, type})
    setTimeout(() => setToast(null), 2500)
  }

  // ───── 待审核数据（用 status=pending 后端分页） ─────
  const loadPending = useCallback(async () => {
    if (activeTab !== 'pending') return
    setLoading(true)
    try {
      const res: any = await pointsApi.records({
        status: 'pending',
        page: currentPage,
        page_size: pageSize
      })
      const items = res.records || []
      setRecords(items)
      setTotal(res.total || 0)
      // 后端响应里直接给出 confirmed/cancelled 数量，无需切到对应 tab 才能更新
      if (typeof res.confirmed_count === 'number') setConfirmedTotal(res.confirmed_count)
    } catch {
      setRecords([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }, [activeTab, currentPage, pageSize])

  // ───── 已确认数据（用 status=confirmed 后端分页） ─────
  const loadConfirmed = useCallback(async () => {
    if (activeTab !== 'confirmed') return
    setLoading(true)
    try {
      const res: any = await pointsApi.records({
        status: 'confirmed',
        page: confirmedPage,
        page_size: confirmedPageSize
      })
      const items = res.records || []
      setConfirmedRecords(items)
      setConfirmedTotal(res.total || 0)
      // 后端响应里直接给出 pending 数量
      if (typeof res.pending_count === 'number') setTotal(res.pending_count)
    } catch {
      setConfirmedRecords([])
      setConfirmedTotal(0)
    } finally {
      setLoading(false)
    }
  }, [activeTab, confirmedPage, confirmedPageSize])

  useEffect(() => { loadPending() }, [loadPending])
  useEffect(() => { loadConfirmed() }, [loadConfirmed])

  // 页码按钮生成
  const getPageNumbers = (total: number, current: number): (number | string)[] => {
    const totalPages = Math.max(1, Math.ceil(total / current))
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1)
    const pages: (number | string)[] = [1]
    if (current > 3) pages.push('...')
    const start = Math.max(2, current - 1)
    const end = Math.min(totalPages - 1, current + 1)
    for (let i = start; i <= end; i++) pages.push(i)
    if (current < totalPages - 2) pages.push('...')
    pages.push(totalPages)
    return pages
  }

  // 检查是否在7天可撤销期限内
  const canRevoke = (record: any) => {
    if (record.status !== 'confirmed') return false
    const createdDate = new Date(record.created_at || record.createdAt || '')
    const now = new Date()
    const diffDays = Math.floor((now.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24))
    return diffDays <= 7
  }

  // 获取剩余可撤销天数
  const getRemainingDays = (record: any) => {
    const createdDate = new Date(record.created_at || record.createdAt || '')
    const now = new Date()
    const diffDays = Math.floor((now.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24))
    return 7 - diffDays
  }

  const handleOpenModal = (record: any, action: 'confirm' | 'cancel' | 'revoke') => {
    setSelectedRecord(record)
    setActionType(action)
    setCancelReason('')
    setShowModal(true)
  }

  const handleConfirm = () => {
    if (!selectedRecord) return
    pointsApi.confirm(selectedRecord.id)
      .then(() => {
        showToast('事件已确认，积分已发放', 'success')
        setShowModal(false)
        setSelectedRecord(null)
        loadPending()
      })
      .catch((err: Error) => showToast(`确认失败：${err.message || '未知错误'}`, 'error'))
  }

  const handleCancel = () => {
    if (!selectedRecord || !cancelReason) return
    pointsApi.cancel(selectedRecord.id, cancelReason)
      .then(() => {
        showToast('事件已驳回', 'success')
        setShowModal(false)
        setSelectedRecord(null)
        loadPending()
      })
      .catch((err: Error) => showToast(`驳回失败：${err.message || '未知错误'}`, 'error'))
  }

  const handleRevoke = () => {
    if (!selectedRecord || !cancelReason) return
    pointsApi.cancel(selectedRecord.id, cancelReason)
      .then(() => {
        showToast('撤销成功，积分已从用户账户扣除', 'success')
        setShowModal(false)
        setSelectedRecord(null)
        loadConfirmed()
      })
      .catch((err: Error) => showToast(`撤销失败：${err.message || '未知错误'}`, 'error'))
  }

  // 待审核分页 props
  const pendingTotalPages = Math.max(1, Math.ceil(total / pageSize))
  const confirmedTotalPages = Math.max(1, Math.ceil(confirmedTotal / confirmedPageSize))

  return (
    <div className="space-y-6">
      {/* Toast 提示 */}
      {toast && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50 px-6 py-3 rounded-xl text-white font-medium shadow-lg"
          style={{backgroundColor: toast.type === 'error' ? '#ef4444' : '#22c55e', minWidth: 200}}>
          {toast.msg}
        </div>
      )}

      {/* 页面标题 */}
      <div className="bg-white rounded-2xl shadow-md p-6">
        <h1 className="text-xl font-bold text-gray-800 flex items-center">
          <Clock className="w-6 h-6 mr-2 text-orange-600" />
          事件审核管理
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          {isAdmin
            ? '审核、确认或撤销全平台医护人员提交的事件'
            : isSupervisor
            ? '审核、确认或撤销医护人员提交的事件'
            : '查看待审核的事件记录'}
        </p>
      </div>

      {/* Tab 切换 */}
      <div className="bg-white rounded-2xl shadow-md p-2 flex">
        <button
          onClick={() => setActiveTab('pending')}
          className={`flex-1 py-3 rounded-xl font-medium transition-colors flex items-center justify-center space-x-2 ${
            activeTab === 'pending' ? 'bg-green-700 text-white' : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <Clock className="w-5 h-5" />
          <span>待审核 ({total})</span>
        </button>
        <button
          onClick={() => setActiveTab('confirmed')}
          className={`flex-1 py-3 rounded-xl font-medium transition-colors flex items-center justify-center space-x-2 ${
            activeTab === 'confirmed' ? 'bg-green-700 text-white' : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <CheckCircle className="w-5 h-5" />
          <span>已确认 ({confirmedTotal})</span>
        </button>
      </div>

      {/* 7天撤销期限提示 */}
      {canOperate && confirmedTotal > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-start space-x-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-blue-800">撤销期限说明</p>
              <p className="text-sm text-blue-600 mt-1">
                已确认的积分记录可在 <strong>7天内</strong> 进行撤销操作，超出期限后将无法撤销。
              </p>
            </div>
          </div>
        </div>
      )}

      {/* 内容区 */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-green-600 animate-spin" />
        </div>
      ) : activeTab === 'pending' ? (
        records.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-md p-12 text-center">
            <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">暂无待审核事件</h3>
            <p className="text-gray-500">所有事件都已处理完毕，干得漂亮!</p>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              {records.map((record) => (
                <div key={record.id} className="bg-white rounded-2xl shadow-md p-6 border-l-4 border-orange-400">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex items-start space-x-4">
                      <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center">
                        <Clock className="w-7 h-7 text-orange-600" />
                      </div>
                      <div>
                        <div className="flex items-center space-x-2 mb-1">
                          <h3 className="font-bold text-gray-800 text-lg">{record.user_name || ''}</h3>
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                            {record.event_type_name || ''}
                          </span>
                        </div>
                        <div className="flex items-center space-x-4 text-sm text-gray-600">
                          <span>手机: {record.user_phone || ''}</span>
                          <span>操作员: {record.operator_name || record.operatorName || ''}</span>
                        </div>
                        <div className="flex items-center space-x-4 text-sm text-gray-500 mt-1">
                          <span>机构: {record.hospital_name || record.hospital || ''}</span>
                          <span>提交时间: {record.created_at || record.createdAt || ''}</span>
                          {record.remark && (
                            <span className="text-orange-600 text-xs ml-2" title={record.remark}>
                              备注: {record.remark.length > 8 ? record.remark.slice(0, 8) + '...' : record.remark}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="text-right mr-4">
                        <p className="text-3xl font-bold text-green-600">+{record.points}</p>
                        <p className="text-sm text-gray-500">积分</p>
                      </div>
                      {canOperate && (
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleOpenModal(record, 'confirm')}
                            className="flex items-center space-x-2 px-4 py-2 bg-green-700 text-white rounded-xl hover:bg-green-800 transition-colors"
                          >
                            <Check className="w-4 h-4" /><span>确认</span>
                          </button>
                          <button
                            onClick={() => handleOpenModal(record, 'cancel')}
                            className="flex items-center space-x-2 px-4 py-2 border border-red-200 text-red-600 rounded-xl hover:bg-red-50 transition-colors"
                          >
                            <X className="w-4 h-4" /><span>驳回</span>
                          </button>
                        </div>
                      )}
                      <button
                        onClick={() => setSelectedRecord(record)}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <Eye className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* 待审核分页 */}
            {total > 0 && (
              <Pagination
                total={total}
                current={currentPage}
                pageSize={pageSize}
                totalPages={pendingTotalPages}
                onPageChange={setCurrentPage}
                onSizeChange={(s) => { setPageSize(s); setCurrentPage(1) }}
                jumpInput={jumpInput}
                setJumpInput={setJumpInput}
              />
            )}
          </>
        )
      ) : confirmedRecords.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-md p-12 text-center">
          <div className="w-20 h-20 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <CheckCircle className="w-10 h-10 text-gray-400" />
          </div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">暂无已确认记录</h3>
          <p className="text-gray-500">确认的事件将显示在这里</p>
        </div>
      ) : (
        <>
          <div className="space-y-4">
            {confirmedRecords.map((record) => (
              <div key={record.id} className="bg-white rounded-2xl shadow-md p-6 border-l-4 border-green-400">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex items-start space-x-4">
                    <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center">
                      <CheckCircle className="w-7 h-7 text-green-600" />
                    </div>
                    <div>
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className="font-bold text-gray-800 text-lg">{record.user_name || ''}</h3>
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">已确认</span>
                        {canRevoke(record) && (
                          <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-medium rounded-full">
                            可撤销(剩{getRemainingDays(record)}天)
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-4 text-sm text-gray-600">
                        <span>手机: {record.user_phone || ''}</span>
                        <span>事件: {record.event_type_name || ''}</span>
                      </div>
                      <div className="flex items-center space-x-4 text-sm text-gray-500 mt-1">
                        <span>机构: {record.hospital_name || record.hospital || ''}</span>
                        <span>确认时间: {record.created_at || record.createdAt || ''}</span>
                        {record.remark && (
                          <span className="text-green-600 text-xs ml-2" title={record.remark}>
                            备注: {record.remark.length > 8 ? record.remark.slice(0, 8) + '...' : record.remark}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="text-right mr-4">
                      <p className="text-3xl font-bold text-green-600">+{record.points}</p>
                      <p className="text-sm text-gray-500">积分</p>
                    </div>
                    {isSupervisor && canRevoke(record) && (
                      <button
                        onClick={() => handleOpenModal(record, 'revoke')}
                        className="flex items-center space-x-2 px-4 py-2 border border-red-200 text-red-600 rounded-xl hover:bg-red-50 transition-colors"
                      >
                        <Undo2 className="w-4 h-4" /><span>撤销</span>
                      </button>
                    )}
                    {!canRevoke(record) && (
                      <span className="text-sm text-gray-400">已超过7天期限</span>
                    )}
                    <button
                      onClick={() => setSelectedRecord(record)}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <Eye className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* 已确认分页 */}
          {confirmedTotal > 0 && (
            <Pagination
              total={confirmedTotal}
              current={confirmedPage}
              pageSize={confirmedPageSize}
              totalPages={confirmedTotalPages}
              onPageChange={setConfirmedPage}
              onSizeChange={(s) => { setConfirmedPageSize(s); setConfirmedPage(1) }}
              jumpInput={confirmedJumpInput}
              setJumpInput={setConfirmedJumpInput}
            />
          )}
        </>
      )}

      {/* 操作模态框 */}
      {showModal && selectedRecord && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-2xl w-full max-w-md mx-4 shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-lg font-bold text-gray-800">
                {actionType === 'confirm' ? '确认事件' : actionType === 'revoke' ? '撤销积分' : '拒绝事件'}
              </h3>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {actionType === 'confirm' ? (
                <>
                  <div className="bg-green-50 rounded-xl p-4 text-center">
                    <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-2" />
                    <p className="text-gray-700">
                      确认给用户 <span className="font-bold">{selectedRecord.user_name || ''}</span> 增加 <span className="font-bold text-green-600">+{selectedRecord.points}</span> 积分？
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div><p className="text-gray-500">事件类型</p><p className="font-medium text-gray-800">{selectedRecord.event_type_name || ''}</p></div>
                      <div><p className="text-gray-500">操作员</p><p className="font-medium text-gray-800">{selectedRecord.operator_name || selectedRecord.operatorName || ''}</p></div>
                      <div><p className="text-gray-500">机构</p><p className="font-medium text-gray-800">{selectedRecord.hospital_name || selectedRecord.hospital || ''}</p></div>
                      <div><p className="text-gray-500">提交时间</p><p className="font-medium text-gray-800">{selectedRecord.created_at || selectedRecord.createdAt || ''}</p></div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="rounded-xl p-4 text-center bg-red-50">
                    <XCircle className="w-12 h-12 text-red-600 mx-auto mb-2" />
                    <p className="text-gray-700">
                      {actionType === 'revoke' ? `确定要撤销给用户 ${selectedRecord.user_name || ''} 的积分吗？` : '确定要拒绝该积分事件吗？'}
                    </p>
                    <p className="text-sm text-red-600 mt-2">
                      {actionType === 'revoke' ? `撤销后将从用户账户扣除 ${selectedRecord.points} 积分` : '拒绝后该事件将不会被计入积分'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      撤销原因 <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      rows={3}
                      placeholder="请输入撤销原因（必填）"
                      value={cancelReason}
                      onChange={(e) => setCancelReason(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
                    />
                  </div>
                </>
              )}
            </div>
            <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t">
              <button onClick={() => setShowModal(false)} className="px-6 py-2 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">取消</button>
              <button
                onClick={actionType === 'confirm' ? handleConfirm : actionType === 'cancel' ? handleCancel : handleRevoke}
                disabled={(actionType === 'cancel' || actionType === 'revoke') && !cancelReason}
                className={`px-6 py-2 rounded-xl text-white font-medium transition-colors ${
                  actionType === 'confirm' ? 'bg-green-700 hover:bg-green-800' : 'bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed'
                }`}
              >
                {actionType === 'confirm' ? '确认通过' : '确认撤销'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 详情模态框 */}
      {selectedRecord && !showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-2xl w-full max-w-lg mx-4 shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-lg font-bold text-gray-800">事件详情</h3>
              <button onClick={() => setSelectedRecord(null)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><p className="text-sm text-gray-500">用户姓名</p><p className="font-medium text-gray-800">{selectedRecord.user_name || ''}</p></div>
                  <div><p className="text-sm text-gray-500">手机号</p><p className="font-medium text-gray-800">{selectedRecord.user_phone || ''}</p></div>
                  <div><p className="text-sm text-gray-500">事件类型</p><p className="font-medium text-gray-800">{selectedRecord.event_type_name || ''}</p></div>
                  <div><p className="text-sm text-gray-500">获得积分</p><p className="font-bold text-green-600">+{selectedRecord.points}</p></div>
                  <div><p className="text-sm text-gray-500">机构</p><p className="font-medium text-gray-800">{selectedRecord.hospital_name || selectedRecord.hospital || ''}</p></div>
                  <div><p className="text-sm text-gray-500">操作员</p><p className="font-medium text-gray-800">{selectedRecord.operator_name || selectedRecord.operatorName || ''}</p></div>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end px-6 py-4 border-t">
              <button onClick={() => setSelectedRecord(null)} className="px-6 py-2 bg-green-700 text-white rounded-xl hover:bg-green-800 transition-colors">关闭</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ───── 通用分页组件 ─────
const Pagination = ({ total, current, pageSize, totalPages, onPageChange, onSizeChange, jumpInput, setJumpInput }: {
  total: number; current: number; pageSize: number; totalPages: number
  onPageChange: (p: number) => void; onSizeChange: (s: number) => void
  jumpInput: string; setJumpInput: (v: string) => void
}) => {
  const getPages = (): (number | string)[] => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1)
    const pages: (number | string)[] = [1]
    if (current > 3) pages.push('...')
    const start = Math.max(2, current - 1)
    const end = Math.min(totalPages - 1, current + 1)
    for (let i = start; i <= end; i++) pages.push(i)
    if (current < totalPages - 2) pages.push('...')
    pages.push(totalPages)
    return pages
  }
  const handleJump = () => {
    const p = parseInt(jumpInput)
    if (!isNaN(p) && p >= 1 && p <= totalPages) {
      onPageChange(p)
      setJumpInput('')
    } else {
      setJumpInput('')
    }
  }
  return (
    <div className="flex flex-wrap items-center justify-between gap-4">
      <div className="flex items-center gap-3 text-sm text-gray-500">
        <span>共 {total} 条</span>
        <select
          value={pageSize}
          onChange={(e) => onSizeChange(Number(e.target.value))}
          className="border border-gray-200 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
        >
          {PAGE_SIZE_OPTIONS.map(s => <option key={s} value={s}>{s} 条/页</option>)}
        </select>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center gap-2">
          <button onClick={() => onPageChange(Math.max(1, current - 1))} disabled={current === 1}
            className="px-3 py-1.5 border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50 transition-colors">
            <ChevronLeft size={16} />
          </button>
          {getPages().map((p, i) =>
            p === '...' ? (
              <span key={`e-${i}`} className="px-2 text-gray-400">...</span>
            ) : (
              <button key={p} onClick={() => onPageChange(p as number)}
                className={`min-w-[36px] px-2 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  current === p ? 'bg-green-700 text-white' : 'border border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}>{p}</button>
            )
          )}
          <button onClick={() => onPageChange(Math.min(totalPages, current + 1))} disabled={current === totalPages}
            className="px-3 py-1.5 border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50 transition-colors">
            <ChevronRight size={16} />
          </button>
          <div className="flex items-center gap-1 ml-2">
            <span className="text-sm text-gray-400">跳至</span>
            <input type="text" value={jumpInput}
              onChange={(e) => setJumpInput(e.target.value.replace(/[^0-9]/g, ''))}
              onKeyDown={(e) => e.key === 'Enter' && handleJump()}
              onBlur={handleJump} placeholder={String(current)}
              className="w-12 border border-gray-200 rounded-lg px-2 py-1 text-sm text-center focus:outline-none focus:ring-1 focus:ring-green-500" />
            <span className="text-sm text-gray-400">页</span>
          </div>
        </div>
      )}
    </div>
  )
}

export default MedicalVerify
